<script>
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
export default {
  props: {
    integrationId: {
      type: String,
      required: true,
    },
  },
  emits: ['add', 'delete'],
  setup(props) {
    const { integration, hasConnectedHooks } = useIntegrationHook(
      props.integrationId
    );
    return { integration, hasConnectedHooks };
  },
  computed: {
    isMcp() {
      return this.integration.id === 'mcp';
    },
    mcpHook() {
      if (!this.isMcp || !this.hasConnectedHooks) return null;
      return this.integration.hooks[0] || null;
    },
    mcpSettings() {
      return this.mcpHook?.settings || null;
    },
    showMcpSavedDetails() {
      return this.isMcp && this.hasConnectedHooks;
    },
    mcpAuthTypeLabel() {
      const t = this.mcpSettings?.mcp_auth_type;
      const keys = {
        none: 'AUTH_NONE',
        bearer: 'AUTH_BEARER',
        header: 'AUTH_HEADER',
      };
      const k = keys[t];
      return k
        ? this.$t(`INTEGRATION_APPS.MCP_CONNECTED.${k}`)
        : t || '—';
    },
    mcpShowsTokenNote() {
      const t = this.mcpSettings?.mcp_auth_type;
      return (
        (t === 'bearer' || t === 'header') &&
        !!this.mcpSettings?.mcp_auth_token_present
      );
    },
    mcpShowsHeaderName() {
      return (
        this.mcpSettings?.mcp_auth_type === 'header' &&
        !!this.mcpSettings?.mcp_auth_header_name
      );
    },
  },
};
</script>

<template>
  <div class="flex-shrink flex-grow overflow-auto p-4">
    <div class="flex flex-col">
      <div
        class="bg-white dark:bg-slate-800 border border-solid border-slate-75 dark:border-slate-700/50 rounded-xl mb-4 p-4"
      >
        <div class="flex">
          <div class="flex h-[6.25rem] w-[6.25rem]">
            <img
              :src="`/dashboard/images/integrations/${integration.id}.png`"
              class="max-w-full rounded-md border border-slate-50 dark:border-slate-700/50 shadow-sm block dark:hidden bg-white dark:bg-slate-900"
            />
            <img
              :src="`/dashboard/images/integrations/${integration.id}-dark.png`"
              class="max-w-full rounded-md border border-slate-50 dark:border-slate-700/50 shadow-sm hidden dark:block bg-white dark:bg-slate-900"
            />
          </div>
          <div class="flex flex-col justify-center m-0 mx-4 flex-1">
            <h3
              class="text-xl font-medium mb-1 text-slate-800 dark:text-slate-100"
            >
              {{ integration.name }}
            </h3>
            <p class="text-slate-700 dark:text-slate-200">
              {{ integration.description }}
            </p>
          </div>
          <div class="flex justify-center items-center mb-0 w-[15%]">
            <div v-if="hasConnectedHooks">
              <div @click="$emit('delete', integration.hooks[0])">
                <woot-button class="nice alert">
                  {{ $t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT') }}
                </woot-button>
              </div>
            </div>
            <div v-else>
              <woot-button class="button nice" @click="$emit('add')">
                {{ $t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT') }}
              </woot-button>
            </div>
          </div>
        </div>

        <div
          v-if="showMcpSavedDetails"
          class="mt-4 pt-4 border-t border-solid border-slate-75 dark:border-slate-700/50"
        >
          <h4
            class="text-sm font-semibold mb-3 text-slate-800 dark:text-slate-100"
          >
            {{ $t('INTEGRATION_APPS.MCP_CONNECTED.TITLE') }}
          </h4>

          <p
            v-if="!mcpSettings"
            class="text-sm text-slate-600 dark:text-slate-300"
          >
            {{ $t('INTEGRATION_APPS.MCP_CONNECTED.NO_DETAILS') }}
          </p>

          <dl
            v-else
            class="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-[minmax(0,11rem)_1fr] text-sm"
          >
            <dt class="text-slate-500 dark:text-slate-400">
              {{ $t('INTEGRATION_APPS.MCP_CONNECTED.NAME') }}
            </dt>
            <dd class="text-slate-800 dark:text-slate-100 break-all">
              {{ mcpSettings.name || '—' }}
            </dd>
            <dt class="text-slate-500 dark:text-slate-400">
              {{ $t('INTEGRATION_APPS.MCP_CONNECTED.SERVER_URL') }}
            </dt>
            <dd class="text-slate-800 dark:text-slate-100 break-all">
              {{ mcpSettings.mcp_server_url || '—' }}
            </dd>
            <dt class="text-slate-500 dark:text-slate-400">
              {{ $t('INTEGRATION_APPS.MCP_CONNECTED.AUTH') }}
            </dt>
            <dd class="text-slate-800 dark:text-slate-100">
              {{ mcpAuthTypeLabel }}
            </dd>
            <template v-if="mcpShowsHeaderName">
              <dt class="text-slate-500 dark:text-slate-400">
                {{ $t('INTEGRATION_APPS.MCP_CONNECTED.HEADER_NAME') }}
              </dt>
              <dd class="text-slate-800 dark:text-slate-100 break-all">
                {{ mcpSettings.mcp_auth_header_name }}
              </dd>
            </template>
            <template v-if="mcpShowsTokenNote">
              <dt class="text-slate-500 dark:text-slate-400" />
              <dd class="text-slate-600 dark:text-slate-400 italic">
                {{ $t('INTEGRATION_APPS.MCP_CONNECTED.TOKEN_SAVED') }}
              </dd>
            </template>
          </dl>
        </div>
      </div>
    </div>
  </div>
</template>
